"use client";

import { useState } from "react";
import Link from "next/link";
import { FaEnvelope, FaArrowLeft, FaCheck } from "react-icons/fa";
import { useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  
  const requestReset = useMutation(api.authActions.requestPasswordReset);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await requestReset({
        email: email.toLowerCase(),
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      
      // Check if the mutation actually succeeded
      if (result && result.success) {
        setSuccess(true);
      } else {
        // Mutation returned success: false
        setError(result?.error || "Failed to send reset email. Please try again.");
        setSuccess(false);
      }
    } catch (err: any) {
      console.error("Password reset error:", err);
      setError(err.message || "Failed to send reset email. Please try again.");
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#030305] flex items-center justify-center p-4 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-[150px]"></div>
          <div className="absolute inset-0 bg-grid opacity-20"></div>
        </div>

        <div className="w-full max-w-md relative z-10">
          <div className="glass-panel rounded-2xl p-8 border border-white/10 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaCheck className="text-green-400 text-2xl" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Check your email
            </h1>
            <p className="text-gray-400 mb-6">
              We've sent a password reset link to{" "}
              <span className="text-white">{email}</span>
            </p>
            <Link
              href="/auth/signin"
              className="inline-flex items-center gap-2 text-kuttos-cyan hover:underline"
            >
              <FaArrowLeft className="text-sm" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030305] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-kuttos-purple/20 rounded-full blur-[150px]"></div>
        <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-kuttos-cyan/15 rounded-full blur-[120px]"></div>
        <div className="absolute inset-0 bg-grid opacity-20"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Back Link */}
        <Link
          href="/auth/signin"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <FaArrowLeft className="text-sm" />
          <span>Back to sign in</span>
        </Link>

        {/* Card */}
        <div className="glass-panel rounded-2xl p-8 border border-white/10">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="h-10 w-10 bg-gradient-to-br from-kuttos-cyan to-kuttos-blue rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">K</span>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Forgot password?
            </h1>
            <p className="text-gray-400">
              No worries, we'll send you reset instructions
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Email
              </label>
              <div className="relative">
                <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError("");
                  }}
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-kuttos-cyan/50 focus:ring-1 focus:ring-kuttos-cyan/50 transition-all"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-cyan py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                  Sending...
                </>
              ) : (
                "Reset password"
              )}
            </button>
          </form>

          {/* Sign Up Link */}
          <p className="text-center text-gray-400 mt-6">
            Don't have an account?{" "}
            <Link
              href="/auth/signup"
              className="text-kuttos-cyan hover:underline"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
